import { useApi } from 'api/api-context';
import { RevisionGetResponse } from 'api/actions/revision-get/revision-get-response';
import { IPipeline } from 'pages/revisions-module/template-editor/editors/pipeline/types';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useMemo } from 'react';

export interface UsePostSubmitActionsResult {
  canImmediatelyApprove: boolean;
  canImmediatelyConfirm: boolean;
  loading: boolean;
}

/**
 * Determines which post-submit actions (approve, confirm) the current user
 * can immediately perform, based on their permissions and the absence of
 * pipeline automations in the relevant state transitions.
 *
 * Approve: finished-waiting-for-approval (5) -> approved-waiting-for-confirmation (7)
 * Confirm: approved-waiting-for-confirmation (7) -> confirmed (8)
 */
export default function usePostSubmitActions(revision: RevisionGetResponse): UsePostSubmitActionsResult {
  const { roleId, userId } = useApi();
  const canApproveRevisions = roleId === ROLE_ADMIN_ID || revision.actionPermissions.canApprove;
  const canAcceptRevisions = roleId === ROLE_ADMIN_ID || revision.actionPermissions.canAccept;
  const loading = false;

  const pipeline = useMemo(
    () => JSON.parse(revision.revisionTemplate?.pipelineScheme || 'null') as IPipeline | null,
    [revision]
  );

  // Any automation defined for the approve transition (5 -> 7) blocks immediate approval,
  // unless the current user is in the automation's bypassUserIds list.
  const hasApproveAutomations = useMemo(
    () =>
      (pipeline?.automations ?? []).some(
        (a) =>
          a.statusSource === 5 &&
          a.statusTarget === 7 &&
          a.type === 'gather-confirmation' &&
          !(userId !== null && (a.bypassUserIds ?? []).includes(userId))
      ),
    [pipeline, userId]
  );

  // Any automation defined for the confirm transition (7 -> 8) blocks immediate confirmation,
  // unless the current user is in the automation's bypassUserIds list.
  const hasConfirmAutomations = useMemo(
    () =>
      (pipeline?.automations ?? []).some(
        (a) =>
          a.statusSource === 7 &&
          a.statusTarget === 8 &&
          a.type === 'gather-confirmation' &&
          !(userId !== null && (a.bypassUserIds ?? []).includes(userId))
      ),
    [pipeline, userId]
  );

  const canImmediatelyApprove = !loading && canApproveRevisions && !hasApproveAutomations;

  // Confirm is only available when approve is also available (confirm follows approve).
  const canImmediatelyConfirm = !loading && canApproveRevisions && canAcceptRevisions && !hasConfirmAutomations;

  return { canImmediatelyApprove, canImmediatelyConfirm, loading };
}
